import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { CloudStore } from "@omni/types/api/store/cloud/store.model";
import { cn } from "@omni/utils/styles";
import { identity } from "lodash-es";
import { useState } from "react";

import { useStoreFullName } from "@/components/Store/utils/useStoreFullName.hook";
import { CartModel } from "@/redux/modules/api/data/cart/@types/cart.model";
import { ProductConfiguration } from "@/utils/productConfiguration/productConfiguration.types";

import { EmtpyGroupComponent } from "./emptyGroup/emptyGroup.component";
import ProductGroupComponent from "./productGroup/productGroup.component";
import { TotalsOverview } from "./totalsOverview/totalsOverview.component";

type Props = {
    urlTarget?: string;
    shoppingCart?: CartModel | null;
    cartLoading?: boolean;
    productLinkingAvailable?: boolean;
    storeDetails?: CloudStore | null | undefined;
    isMiniCart?: boolean;
    isUpdatingCart?: { [key: string]: boolean };
    activeId?: string;
    productConfiguration?: ProductConfiguration;
    className?: string;
    removeFromCart?: (id: string, quantity: number, free: boolean) => void;
    closeCallback?: () => void;
};

const ShoppingCartSummary = ({
    shoppingCart,
    productLinkingAvailable = false,
    isMiniCart = false,
    storeDetails,
    isUpdatingCart = {},
    className,
    removeFromCart
}: Props) => {
    const [optimisticCart, setOptimisticCart] = useState<CartModel | null>(
        shoppingCart ?? null
    );

    const storeFullName = useStoreFullName(storeDetails);

    const loading = Object.values(isUpdatingCart).some(identity);

    const collectProducts = optimisticCart?.items.filter(
        (i) =>
            i.deliveryType === DeliveryType.ClickAndCollect &&
            !i.isAdditionalService
    );

    const deliveryProducts = optimisticCart?.items.filter(
        (i) =>
            i.deliveryType === DeliveryType.HomeDelivery &&
            !i.isAdditionalService
    );

    const handleOnRemoveFromCart = removeFromCart
        ? (id: string, quantity: number, free: boolean) => {
              setOptimisticCart((prev) => {
                  if (!prev) return null;

                  return {
                      ...prev,
                      items: prev?.items.filter((item) => item.id !== id)
                  };
              });

              removeFromCart(id, quantity, free);
          }
        : undefined;

    if (!optimisticCart?.items?.length) {
        return <EmtpyGroupComponent />;
    }

    return (
        <div
            className={cn(
                "tw:h-full tw:flex tw:flex-col tw:justify-between",
                className
            )}
        >
            <section
                className={cn("tw:px-4", {
                    "tw:h-fit tw:overflow-x-hidden tw:overflow-y-auto":
                        isMiniCart
                })}
            >
                {collectProducts!.length > 0 && (
                    <ProductGroupComponent
                        title={"general.cart.label.clickandcollect"}
                        icon={"la-hand-pointer"}
                        cartItems={collectProducts!}
                        isClickAndCollect={true}
                        loading={loading}
                        isMiniCart={isMiniCart}
                        productLinkingAvailable={productLinkingAvailable}
                        removeFromCart={handleOnRemoveFromCart}
                        shopName={storeFullName}
                    />
                )}
                {deliveryProducts!.length > 0 && (
                    <ProductGroupComponent
                        title={"general.cart.label.delivery"}
                        icon={"la-truck"}
                        cartItems={deliveryProducts!}
                        isClickAndCollect={false}
                        loading={loading}
                        isMiniCart={isMiniCart}
                        productLinkingAvailable={productLinkingAvailable}
                        removeFromCart={handleOnRemoveFromCart}
                        shopName={storeFullName}
                    />
                )}
            </section>
            <TotalsOverview shoppingCart={shoppingCart} />
        </div>
    );
};

export { ShoppingCartSummary };
